// The state behind one master import (code review G-10): read a file into the
// server's preview, then apply that preview by its token. The dialog frame is
// `components/ImportDialog.tsx`.
import { useState } from 'react'
import { errorMessage, isApiError } from '../api/errors'

export interface ImportFlow<P> {
  fileName: string
  reading: boolean
  applying: boolean
  error: string | null
  preview: P | null
  onFile: (file: File) => void
  apply: () => Promise<void>
  reset: () => void
}

/** The state behind one import: read a file into a preview, apply it by its token. */
export function useImportFlow<P extends { preview_token: string }, R>({
  preview,
  commit,
  summarize,
  onApplied,
  tooLarge,
}: {
  preview: (file: File) => Promise<P>
  commit: (previewToken: string) => Promise<R>
  /** The toast after an apply, e.g. "3 added and 2 updated". */
  summarize: (result: R) => string
  onApplied: (summary: string) => void
  /** Refused before it is sent, where the import has a size limit. */
  tooLarge?: { maxBytes: number; message: string }
}): ImportFlow<P> {
  const [fileName, setFileName] = useState('')
  const [reading, setReading] = useState(false)
  const [applying, setApplying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [plan, setPlan] = useState<P | null>(null)

  function reset() {
    setFileName('')
    setPlan(null)
    setError(null)
  }

  async function read(file: File) {
    reset()
    setFileName(file.name)
    if (tooLarge && file.size > tooLarge.maxBytes) {
      setError(tooLarge.message)
      return
    }
    setReading(true)
    try {
      setPlan(await preview(file))
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setReading(false)
    }
  }

  async function apply() {
    if (!plan) return
    setApplying(true)
    setError(null)
    try {
      onApplied(summarize(await commit(plan.preview_token)))
      reset()
    } catch (e) {
      setError(
        isApiError(e) && e.status === 409
          ? `${e.message} Upload the file again to build a fresh preview.`
          : errorMessage(e),
      )
    } finally {
      setApplying(false)
    }
  }

  return { fileName, reading, applying, error, preview: plan, onFile: (f) => void read(f), apply, reset }
}

